import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import { searchProductTotalQuery } from '@/services/graphql'
import { transactionalQueryPreset } from '@/services/query-presets'

const client = getAmplifyClient()

export const planUsageKeys = {
  all: ['plan-usage'] as const,
  soloProducts: (
    userID: string | null | undefined,
    workspaceOrganizationID?: string | null,
  ) =>
    [
      ...planUsageKeys.all,
      'solo-products',
      userID,
      workspaceOrganizationID,
    ] as const,
}

export const countSoloProducts = async (
  userID: string,
  workspaceOrganizationID?: string | null,
): Promise<number> => {
  // Match the personal inventory scope: legacy resources without an
  // organization plus resources belonging to this exact SOLO workspace.
  const organizationFilter = workspaceOrganizationID
    ? {
        or: [
          { organizationID: { exists: false } },
          { organizationID: { eq: workspaceOrganizationID } },
        ],
      }
    : { organizationID: { exists: false } }
  const { data, errors } = (await client.graphql({
    query: searchProductTotalQuery,
    variables: {
      filter: {
        byUserID: { eq: userID },
        type: { ne: 'deleted' },
        nature: { ne: 'service' },
        ...organizationFilter,
      },
    },
  })) as GraphQLResult<{
    searchProductStatusV2?: { total?: number | null } | null
  }>

  const total = data?.searchProductStatusV2?.total
  if (
    errors?.length ||
    !Number.isInteger(total) ||
    total == null ||
    total < 0
  ) {
    throw new Error('Solo product usage is unavailable')
  }
  return total
}

/**
 * Solo workspaces have no organization entitlements, so the number of
 * resources owned by the user is counted directly from the search index.
 */
export const useSoloProductCount = (
  userID: string | null | undefined,
  enabled: boolean,
  workspaceOrganizationID?: string | null,
) =>
  useQuery({
    queryKey: planUsageKeys.soloProducts(userID, workspaceOrganizationID),
    queryFn: () => countSoloProducts(userID!, workspaceOrganizationID),
    enabled: enabled && Boolean(userID),
    ...transactionalQueryPreset,
    refetchOnMount: true,
  })
